import { useMsal } from "@azure/msal-react";
import PropTypes from "prop-types";
import {
    Button,
    CircularProgress, Dialog, DialogActions, DialogContent, IconButton,
    List,
    ListItem, ListItemIcon, ListItemText, TextField, Tooltip
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
    ArrowLeft, Build, DriveEta, ModeComment
} from "@material-ui/icons";
import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { globalStyles } from "../../assets/globalStyles";
import LineChart from "../../features/chart/LineChart";
import { selectDate } from "../../features/konfiguration/konfigurationSlice";
import { angeschlosseneAnlage, anlagentyp } from "../../utils/apiTypes";
import { toUTCDateString } from '../../utils/utils';
import {
    getGespeicherteSuchen, selectAngeschlosseneAnlagen, selectAnlagentypen, selectUmspannwerkLoading, setShowAngeschlosseneAnlagen
} from "./netzwirtschaftSlice";
import { useStyles } from "./netzwirtschaftStyles";
import { TabData } from "./netzwirtschaftTypes";
import { selectMessstellenLookup } from '../../features/messstellenLookup/messstellenLookupSlice';
import InfoIcon from '@material-ui/icons/Info';

export interface GraphenProps {
    tabs: TabData[],
    layout: string;
}


export const NetzwirtschaftGraphen: React.FC<GraphenProps> = ({ tabs, layout }) => {
    const dispatch = useAppDispatch();
    const { instance } = useMsal();

    const messstellenLookup = useAppSelector(selectMessstellenLookup);

    function getSchaltfeld(name: string): string {
        const currentMessstelle = messstellenLookup.find(x => x.name == name)
        if (currentMessstelle)
            return currentMessstelle.schaltfeld;
        return "";
    }

    function getKommentar(name: string): string {
        const currentMessstelle = messstellenLookup.find(x => x.name == name)
        console.log(currentMessstelle?.kommentar)
        if (currentMessstelle && currentMessstelle.kommentar != null)
            return currentMessstelle.kommentar;
        return "";
    }

    useEffect(() => {
        dispatch(getGespeicherteSuchen());
    }, [instance]);

    useEffect(() => {
        //eslint-disable-next-line scanjs-rules/call_addEventListener
        document.getElementById("chartDiv")?.addEventListener("wheel", e => e.preventDefault());
    });

    const classes = {
        ...useStyles(),
        ...globalStyles(),
    };

    const print = useMediaQuery("print");
    const uwLoading: boolean = useAppSelector(selectUmspannwerkLoading);
    const isUTC = useAppSelector(selectDate);
    const anlagentypen: anlagentyp[] = useAppSelector(selectAnlagentypen);
    const angeschlosseneAnlagen: angeschlosseneAnlage[] = useAppSelector(selectAngeschlosseneAnlagen);

    const [showAngeschlossenenAnlagen, setShowAngeschlossenenAnlagen] = React.useState(false)

    return (
        <Grid container spacing={1} className={classes.print}>
            {tabs.map((e, i) => (
                <React.Fragment key={i}>
                    <Grid item xs={layout == "double" && !print ? 6 : 12} style={{ flexShrink: 1, flexGrow: 1, flexBasis: !e.showAngeschlosseneAnlagen && !(layout == "double" && !print) ? "auto" : "100%" }} key={i}>
                        {/* <Grid item xs={12} style={{ flexShrink: 1, flexGrow: 1, flexBasis: !e.showAngeschlosseneAnlagen && !(layout == "double" && !print) ? "auto" : "100%" }} key={i}> */}
                        <div>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                <Typography style={{ marginRight: "12px" }} component={"div"} className={classes.noPrint}>
                                    {e.fields.messpunkt && e.fields.rekorder.beschreibung
                                        ? e.fields.uw + " " + getSchaltfeld(e.fields.messpunkt) + " " + e.fields.rekorder.beschreibung
                                        : e.name}
                                </Typography>
                                {!e.fields.uw || angeschlosseneAnlagen.filter(x => x.umspannwerk == e.fields.uw).length == 0 ? "" : <Tooltip title="Zusätzliche Informationen anzeigen"><InfoIcon color={"secondary"} style={{ cursor: "pointer" }} onClick={() => setShowAngeschlossenenAnlagen(true)} /></Tooltip>}
                            </div>
                            <Typography component={"div"} className={classes.onlyPrint}>
                                <Grid container className={classes.printDetails}>
                                    <Grid item xs={2}>
                                        Umspannwerk:{" "}
                                    </Grid>
                                    <Grid item xs={4}>
                                        {e.fields.uw}
                                    </Grid>
                                    <Grid item xs={2}>
                                        Messpunkt:{" "}
                                    </Grid>
                                    <Grid item xs={4}>
                                        {e.fields.messpunkt}
                                    </Grid>
                                    <Grid item xs={2}>
                                        Startzeitpunkt:{" "}
                                    </Grid>
                                    <Grid item xs={4}>
                                        {e.fields.startDatum
                                            ? [isUTC ? toUTCDateString(new Date(e.fields.startDatum)) : new Date(e.fields.startDatum).toLocaleString(
                                                "de-DE",
                                                {
                                                    year: "numeric",
                                                    month: "2-digit",
                                                    day: "2-digit",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                    second: "2-digit",
                                                })]
                                            : ""}
                                    </Grid>
                                    <Grid item xs={2}>
                                        Endzeitpunkt:{" "}
                                    </Grid>
                                    <Grid item xs={4}>
                                        {e.fields.endDatum
                                            ? [isUTC ? toUTCDateString(new Date(e.fields.endDatum)) : new Date(e.fields.endDatum).toLocaleString(
                                                "de-DE",
                                                {
                                                    year: "numeric",
                                                    month: "2-digit",
                                                    day: "2-digit",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                    second: "2-digit",
                                                }
                                            )]
                                            : ""}
                                    </Grid>
                                    <Grid item xs={2}>
                                        Rekorder:{" "}
                                    </Grid>
                                    <Grid item xs={4}>
                                        {e.fields.rekorder.beschreibung +
                                            " - " +
                                            e.fields.rekorder.kategorie}
                                    </Grid>
                                    <Grid item xs={2}>
                                        Messgrößen:{" "}
                                    </Grid>
                                    <Grid item xs={4}>
                                        {e.fields.messgroesse.map(x => x.beschreibung + " (" + x.feldname + ")").join(", ")}
                                    </Grid>
                                </Grid>
                            </Typography>
                            {e.graphLoading ? (
                                <CircularProgress />
                            ) : e.graphData.series.length > 0 ? (
                                LineChart(e.graphData, isUTC, e.index.toString())
                            ) : (
                                <React.Fragment>
                                    <Grid
                                        item
                                        md={12}
                                        style={{
                                            textAlign: "center",
                                            marginRight: "20px",
                                            marginLeft: "50px",
                                            paddingTop: "75px",
                                        }}
                                    >
                                        <div style={{ height: "30vh" }}>
                                            <Typography>Bitte Filter definieren, um Daten anzuzeigen.</Typography>
                                        </div>
                                    </Grid>
                                </React.Fragment>
                            )}
                        </div>
                    </Grid>
                    <Tooltip title="Angeschlossene Anlagen anzeigen"><IconButton className={classes.noPrint} style={{ float: "right", padding: "2px", display: e.showAngeschlosseneAnlagen || (layout == "double" && !print) ? "none" : "" }} onClick={(ev: React.MouseEvent) => {
                        ev.stopPropagation(); dispatch(setShowAngeschlosseneAnlagen(e.index));
                    }}><ArrowLeft style={{ fontSize: "3.5rem" }} /></IconButton></Tooltip>

                    {showAngeschlossenenAnlagen ? <Dialog
                        open={true}
                        maxWidth={false}
                    >
                        {/* <DialogTitle>Angeschlossene Anlagen anzeigen</DialogTitle> */}
                        <DialogContent style={{ width: "60em" }}>
                            <Grid item xs={12} key={i} className={classes.noPrint} hidden={!e.showAngeschlosseneAnlagen || (layout == "double" && !print)}>
                                {uwLoading ? <CircularProgress size="30px" style={{ alignSelf: "center", padding: "5px" }} /> : (
                                    <div>
                                        {!e.fields.uw || angeschlosseneAnlagen.filter(x => x.umspannwerk == e.fields.uw).length == 0 ? "Keine angeschlossenen Anlagen" :
                                            <React.Fragment>
                                                <Grid container xs={12} >
                                                    <Grid item xs={6}>
                                                        <Typography variant="h6" style={{ textAlign: "left" }}>Kunden</Typography>
                                                        <List>
                                                            {angeschlosseneAnlagen.filter(anlage => anlage.anlagentyp == anlagentypen.find(typ => typ.typ == "Kunde")?.id && anlage.umspannwerk == e.fields.uw).length !== 0 ? angeschlosseneAnlagen.filter(anlage => anlage.anlagentyp == anlagentypen.find(typ => typ.typ == "Kunde")?.id && anlage.umspannwerk == e.fields.uw).map(x => (
                                                                <ListItem key={x.name}>
                                                                    <ListItemIcon><DriveEta /></ListItemIcon>
                                                                    <ListItemText primary={x.name} />
                                                                </ListItem>
                                                            )) : "Keine Kunde"}
                                                        </List>
                                                        <Typography variant="h6" style={{ textAlign: "left" }}>Eigenanlagen</Typography>
                                                        <List>
                                                            {angeschlosseneAnlagen.filter(anlage => anlage.anlagentyp == anlagentypen.find(typ => typ.typ == "Eigenanlage")?.id && anlage.umspannwerk == e.fields.uw).length !== 0 ? angeschlosseneAnlagen.filter(anlage => anlage.anlagentyp == anlagentypen.find(typ => typ.typ == "Eigenanlage")?.id && anlage.umspannwerk == e.fields.uw).map(x => (
                                                                <ListItem key={x.name}>
                                                                    <ListItemIcon><Build /></ListItemIcon>
                                                                    <ListItemText primary={x.name} />
                                                                </ListItem>
                                                            )) : "Keine Eigenanlage"}
                                                        </List>
                                                    </Grid>
                                                    {e.fields.messpunkt != "" ?
                                                        <Grid xs={6}>
                                                            <div style={{ display: "flex", alignItems: "center", marginBottom: "1em" }}><ModeComment color="disabled" style={{ marginRight: "12px" }} /><Typography variant="h6" style={{ textAlign: "left" }}>Kommentar</Typography></div>
                                                            <TextField
                                                                multiline
                                                                rows={9}
                                                                rowsMax={9}
                                                                // variant="outlined"
                                                                fullWidth={true}
                                                                value={getKommentar(e.fields.messpunkt) !== "" ? getKommentar(e.fields.messpunkt) : "Keine Kommentar"} />
                                                        </Grid> : ""}
                                                </Grid>
                                            </React.Fragment>}
                                    </div>
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                color="secondary"
                                variant="contained"
                                onClick={() => setShowAngeschlossenenAnlagen(false)}
                                style={{ marginRight: "12px", marginBottom: "12px" }}
                            >
                                Schliessen
                            </Button>
                        </DialogActions>
                    </Dialog> : ""}
                </React.Fragment>
            ))
            }
        </Grid >
    );
}
NetzwirtschaftGraphen.propTypes = {
    tabs: PropTypes.array.isRequired,
    layout: PropTypes.string.isRequired
};
